import React from 'react';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import type { Comparison } from '../../../../../types/BaselineMeasurements';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import formatter from 'app/utils/formatter';
import { TranslationKeys } from '../../../../../translations';
import { ActionButton } from '../../../../../components';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

type ComparisonStatusProps = {
  comparisonData?: Comparison;
  isLoading?: boolean;
  markAsComplete: () => void;
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'pending':
      return {
        icon: <WarningIcon />,
        label: 'Pending',
        backgroundColor: 'warning.light',
      };
    case 'completed':
      return {
        icon: <CheckCircleIcon />,
        label: 'Completed',
        backgroundColor: 'success.light',
      };
    case 'processing':
      return {
        icon: <ScheduleIcon />,
        label: 'Syncing',
        backgroundColor: 'warning.light',
      };
    case 'failed':
      return {
        icon: <CancelIcon />,
        label: 'Failed',
        backgroundColor: 'error.light',
      };
    default:
      return {
        icon: <CheckCircleIcon />,
        label: status,
        backgroundColor: 'transparent',
      };
  }
};

const StatusChip = ({ status }: { status: string }) => {
  const config = getStatusConfig(status);

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 400,
        color: 'text.primary',
        px: 0.5,
        backgroundColor: config.backgroundColor || 'transparent',
      }}
    />
  );
};

export const ComparisonStatusOverview = ({ comparisonData, isLoading, markAsComplete }: ComparisonStatusProps) => {
  const status = comparisonData?.status || 'pending';
  const insurerStatus = comparisonData?.insurerDataStatus || 'pending';
  const lastUpdate = formatter.date(comparisonData?.lastUpdate);
  const insurerDataLastUpdate = formatter.date(comparisonData?.insurerDataLastUpdate);

  return (
    <Box sx={{ p: 1, display: 'flex', alignItems: 'stretch', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 800 }}>
          {'COMPARISON'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip status={status} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {lastUpdate}
          </Typography>
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ opacity: 1, borderColor: '#c5d9e299', borderWidth: 0.5 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 800 }}>
          {'INSURER DATA'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StatusChip status={insurerStatus} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            {insurerDataLastUpdate}
          </Typography>
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ opacity: 1, borderColor: '#c5d9e299', borderWidth: 0.5 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
        <ActionButton
          icon={faCheckCircle}
          data-testid={'comparison-mark-as-complete'}
          messageId={TranslationKeys.connection_processes_compareBatch_markAsComplete}
          disabled={isLoading || false}
          onClick={markAsComplete}
        />
      </Box>
    </Box>
  );
};
